'use client';

import { Button } from '@/components/ui/button';
import { AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

type ErrorDisplayVariant = 'default' | 'destructive' | 'warning' | 'info';

interface ErrorDisplayProps {
  error: string;
  onClear?: () => void;
  className?: string;
  variant?: ErrorDisplayVariant;
  showIcon?: boolean;
  showClearButton?: boolean;
}

export function ErrorDisplay({ 
  error, 
  onClear, 
  className,
  variant = 'destructive',
  showIcon = true,
  showClearButton = true 
}: ErrorDisplayProps) {
  if (!error) return null;

  const variantStyles = {
    default: 'bg-muted border-border text-foreground',
    destructive: 'bg-destructive/10 border-destructive/20 text-destructive',
    warning: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-700 dark:text-yellow-400',
    info: 'bg-blue-500/10 border-blue-500/20 text-blue-700 dark:text-blue-400',
  };

  const variantIcons = {
    default: AlertTriangle,
    destructive: AlertTriangle,
    warning: AlertTriangle,
    info: Info,
  };

  const Icon = variantIcons[variant];

  return (
    <div 
      role="alert"
      aria-live="polite"
      data-testid="error-display"
      className={cn(
        'border rounded-lg p-4',
        variantStyles[variant],
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {showIcon && (
            <Icon className="h-4 w-4 flex-shrink-0" />
          )}
          <p className="text-sm"><span>{error}</span></p>
        </div>
        {showClearButton && onClear && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="size-6 p-0 hover:bg-transparent max-md:size-10"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Hook-based error display component
interface HookErrorDisplayProps {
  errorState: {
    hasError: boolean;
    errorMessage: string;
    isValidationWarning?: boolean;
  };
  onClear: () => void;
  className?: string;
  variant?: ErrorDisplayVariant;
}

export function HookErrorDisplay({ 
  errorState, 
  onClear, 
  className,
  variant
}: HookErrorDisplayProps) {
  if (!errorState.hasError) return null;

  const resolvedVariant = variant ?? (errorState.isValidationWarning ? 'warning' : 'destructive');

  return (
    <ErrorDisplay
      error={errorState.errorMessage}
      onClear={onClear}
      className={className}
      variant={resolvedVariant}
    />
  );
}
